import type { DiveObject, I18nText, Locale } from "@divespots/contracts";
import { LOCALES, pickLocale } from "@divespots/contracts";

type ResolvedDepth = DiveObject["depth"];

/** Swiss regional variants: decimal point in de/it/en, as in "36.4 m". */
const INTL_TAG: Record<Locale, string> = { de: "de-CH", en: "en-CH", fr: "fr-CH", it: "it-CH" };

/** Narrow no-break space between number and unit (design: keep them together). */
export const NNBSP = " ";

export function formatNumber(value: number, locale: Locale, decimals: number): string {
  return new Intl.NumberFormat(INTL_TAG[locale], {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** A timestamp (ISO, UTC) as the calendar day it is in Switzerland: 22:30Z is already the next day. */
export function formatTimestamp(iso: string, locale: Locale): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(INTL_TAG[locale], {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: "Europe/Zurich",
  }).format(d);
}

/** "31.10.26": compact table form of `formatTimestamp` (Zurich day, two-digit year). */
export function formatTimestampShort(iso: string, locale: Locale): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(INTL_TAG[locale], {
    day: "numeric",
    month: "numeric",
    year: "2-digit",
    timeZone: "Europe/Zurich",
  }).format(d);
}

/** "27.9.2026": a calendar day (YYYY-MM-DD) as the Swiss locale writes it, independent of the time zone. */
export function formatDate(isoDay: string, locale: Locale): string {
  const d = new Date(`${isoDay.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDay;
  return new Intl.DateTimeFormat(INTL_TAG[locale], {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

/** "18 MB", "640 kB": storage sizes for the offline feature (decimal units, whole numbers from 10 MB). */
export function formatBytes(bytes: number, locale: Locale): string {
  const mb = bytes / 1_000_000;
  const fmt = (n: number, d: number, unit: string) =>
    `${formatNumber(n, locale, d)}${NNBSP}${unit}`;
  if (mb >= 10) return fmt(Math.round(mb), 0, "MB");
  if (mb >= 1) return fmt(mb, 1, "MB");
  return fmt(Math.max(1, Math.round(bytes / 1000)), 0, "kB");
}

/** "36.4 m" (one decimal, measured or computed) or "40 m" (decimals: 0, max depth). */
export function formatMetres(value: number, locale: Locale, decimals = 1): string {
  return `${formatNumber(value, locale, decimals)}${NNBSP}m`;
}

/** Metres above sea level etc. use the same unit style. */
export const formatMetresWhole = (value: number, locale: Locale) => formatMetres(value, locale, 0);

/**
 * Depth of an object. `null` when unknown: callers show "Keine Daten", never "0 m".
 * Computed values get a leading "≈" (the lakebed model is not a measurement).
 */
export function formatResolvedDepth(depth: ResolvedDepth, locale: Locale): string | null {
  if (depth.valueM === null) return null;
  const text = formatMetres(depth.valueM, locale, 1);
  return depth.source === "computed" ? `≈${NNBSP}${text}` : text;
}

/** "MM-DD" (recurring) → "28.2." in the active locale. */
export function formatMonthDay(md: string, locale: Locale): string {
  const [m, d] = md.split("-").map(Number) as [number, number];
  return new Intl.DateTimeFormat(INTL_TAG[locale], {
    day: "numeric",
    month: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(2000, m - 1, d)));
}

export function formatCoordinate(p: { lat: number; lon: number }): string {
  return `${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}`;
}

/** Content text in the active locale (fallback de → en → fr → it) plus the locale it actually came from. */
export function pickText(
  text: I18nText | null | undefined,
  locale: Locale,
): { value: string; lang: Locale } | null {
  const value = pickLocale(text, locale);
  if (value === null || !text) return null;
  const lang = [locale, ...LOCALES].find((l) => text[l] === value) ?? locale;
  return { value, lang };
}

/** "3.11.26, 14:05": compact Zurich date and time of an audit entry. */
export function formatDateTime(iso: string, locale: Locale): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(INTL_TAG[locale], {
    day: "numeric",
    month: "numeric",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Zurich",
  }).format(d);
}
